import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Heart, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const values = [
  { icon: Shield, title: "Fiabilité", desc: "Chaque engagement est tenu. Les délais sont respectés, les budgets maîtrisés, la qualité garantie par notre assurance décennale." },
  { icon: Heart, title: "Passion du métier", desc: "La rénovation est un art que nous pratiquons avec exigence. Chaque chantier est l'occasion de démontrer notre savoir-faire." },
  { icon: Clock, title: "Respect des délais", desc: "Une planification rigoureuse et une coordination éprouvée des corps de métier pour livrer vos travaux dans les temps." },
  { icon: Users, title: "Ecoute client", desc: "Votre vision est notre fil conducteur. Nous adaptons nos solutions à vos besoins, votre budget et vos contraintes spécifiques." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Notre histoire</motion.p>
              <motion.h1 variants={fadeIn} className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-[#2C2C2C] mt-4">26 ans de savoir-faire</motion.h1>
              <motion.div variants={fadeIn} className="section-divider mt-8 mb-8" />
              <motion.p variants={fadeIn} className="text-lg font-light leading-relaxed text-[#2C2C2C]/70">
                Depuis 2000, 2BW BAT accompagne les propriétaires parisiens dans la transformation de leurs intérieurs. Fondée sur une passion authentique pour le bâtiment, notre entreprise a bâti sa réputation sur la qualité irréprochable de ses réalisations et le respect absolu de ses engagements.
              </motion.p>
              <motion.p variants={fadeIn} className="text-lg font-light leading-relaxed text-[#2C2C2C]/70 mt-6">
                Basés dans le 14e arrondissement, nous intervenons sur l'ensemble de Paris et de l'Ile-de-France. Notre connaissance approfondie du parc immobilier parisien &mdash; de l'immeuble haussmannien aux résidences contemporaines &mdash; nous permet d'apporter des solutions techniques adaptées à chaque projet.
              </motion.p>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="relative">
              <div className="img-zoom">
                <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80&fm=webp" alt="Artisan au travail sur un chantier parisien" className="w-full h-[500px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#C5A065] text-white p-8 hidden lg:block">
                <p className="font-heading text-4xl font-medium">26</p>
                <p className="text-sm tracking-widest uppercase mt-1">Années<br />d'expérience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#E5E0D8]/30" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.div variants={fadeIn} className="text-center mb-20">
              <p className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Nos valeurs</p>
              <h2 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#2C2C2C] mt-4">Ce qui nous définit</h2>
              <div className="section-divider mx-auto mt-6" />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((v, i) => (
                <motion.div key={i} variants={fadeIn} className="bg-white p-8 border border-border/50 hover:border-[#C5A065]/30 transition-colors duration-300">
                  <v.icon className="w-8 h-8 text-[#C5A065] stroke-[1.5] mb-6" />
                  <h3 className="font-heading text-xl font-medium text-[#2C2C2C] mb-3">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-[#8A8A8A]">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeIn} className="font-heading text-4xl md:text-5xl font-normal text-white tracking-tight">Confiez-nous votre projet</motion.h2>
            <motion.p variants={fadeIn} className="text-base text-white/60 max-w-lg mx-auto mt-6 mb-12">
              26 ans d'expérience à votre service. Discutons ensemble de votre prochain projet de rénovation.
            </motion.p>
            <motion.div variants={fadeIn}>
              <Link to="/contact">
                <Button className="group bg-[#C5A065] text-white hover:bg-[#C5A065]/90 px-10 py-6 text-sm tracking-widest uppercase rounded-none">
                  Nous contacter
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
